import React, { useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { ethers } from 'ethers';
import { getFactoryAddress,getNativeToken, getBlockExplorerUrl, getWSSUrl } from '../config/contracts';
import { usePublicClient, useChainId, useAccount } from 'wagmi';
import YOLOGame from '../config/YOLOGame.json';
import YOLOToken from '../config/YOLOToken.json';
import { useNavigate } from 'react-router-dom';
import ProgressBar from './common/ProgressBar';
import { useTranslation } from 'react-i18next';
import { commitmentToVersionedHash } from 'viem';

const GameListContainer = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 20px auto;
  padding: 0 20px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 15px;
    margin: 15px auto;
  }
`;

const GameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const GameCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px;
  transition: all 0.3s;
  box-sizing: border-box;
  width: 100%;
  cursor: pointer;
  
  &:hover {
    border-color: #4ad0ac;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    gap: 15px;
    padding: 15px;
  }
`;

const GameContent = styled.div`
  display: flex;
  gap: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const GameImage = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 150px;
  }
`;

const GameInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  min-width: 0;
  width: 100%;
`;

const TokenName = styled.div`
  font-size: 20px;
  color: #4ad0ac;
  font-weight: bold;
  margin: 0;
  display: inline-block;
`;

const RoundInfo = styled.div<{ isActive?: boolean, phase?: string }>`
  font-size: 16px;
  font-weight: bold;
  margin: 0;
  display: inline-block;
  margin-left: 12px;
  color: ${props => {
    if (!props.isActive) return 'rgba(255, 255, 255, 0.2)';
    switch (props.phase) {
      case 'minting':
        return '#4CAF50'; // 你想要的 minting 字体色
      case 'burning':
        return '#F44336'; // 你想要的 burning 字体色
      default:
        return 'rgba(255, 255, 255, 0.9)';
      }
    }};
`;

const TokenHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AddressInfo = styled.a`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  text-align: left;
  word-break: break-all;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #4ad0ac;
  }
`;

const AddressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
`;

const Description = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MinCap = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  word-break: break-all;
`;

const ProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const ProgressBarWrapper = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const FristRound = styled.div`
  color: #4ad0ac;
  font-size: 12px;
  white-space: nowrap;
`;

interface GameInfo {
  gameAddress: string;
  createrAddress: string;
  tokenAddress: string;
  minCap: string;
  totalRound: number;
  currentRound: number;
  isRoundActive: boolean;
  tokenName: string;
  tokenSymbol: string;
  description: string;
  logoUrl: string;
  tgLink: string;
  webLink: string;
  xLink: string;
  currentTime: string;
  currentRoundId: string;
  phase: string;
  firstRoundInfo: {
    totalParticipants: string;
    totalDeposit: string;
    totalBurned: string;
    rewardPool: string;
    isActive: boolean;
    startTime: string;
    mintEndedTime: string;
    burnEndTime: string;
    isClaimable: boolean;
    isRoundEnded: boolean;
    isSkipped: boolean;
    minCapEnded: boolean;
    tokenName: string;
    tokenSymbol: string;
    description: string;
    logoUrl: string;
    tgLink: string;
    webLink: string;
    xLink: string;
  } | null;
}

const formatAddress = (address: string) => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

// 全局变量存储游戏数据，分别缓存所有游戏、创建者游戏和收藏游戏
let globalGamesCache: {
  allGames?: { 
    data: GameInfo[], 
    timestamp: number,
    nextTimestamp: number,
    hasMore: boolean 
  };
  creatorGames: { 
    [address: string]: { 
      data: GameInfo[], 
      timestamp: number,
      nextTimestamp: number,
      hasMore: boolean 
    } 
  };
  favoriteGames: { 
    [address: string]: { 
      data: GameInfo[], 
      timestamp: number,
      nextTimestamp: number,
      hasMore: boolean 
    } 
  };
} = {
  creatorGames: {},
  favoriteGames: {}
};

// 全局变量存储图片加载失败状态
let globalFailedImages: { [key: string]: boolean } = {};

const SkeletonPulse = styled.div`
  animation: pulse 1.5s ease-in-out infinite;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.1) 37%, rgba(255, 255, 255, 0.05) 63%);
  background-size: 400% 100%;

  @keyframes pulse {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }
`;

const SkeletonCard = styled(GameCard)`
  pointer-events: none;
`;

const SkeletonImage = styled(SkeletonPulse)`
  width: 150px;
  height: 150px;
  border-radius: 8px;

  @media (max-width: 768px) {
    width: 100%;
    height: 150px;
  }
`;

const SkeletonText = styled(SkeletonPulse)`
  height: 20px;
  border-radius: 4px;
  margin-bottom: 8px;
`;

const SkeletonShortText = styled(SkeletonText)`
  width: 60%;
`;

const LoadingGameList = () => {
  return (
    <GameGrid>
      {Array(6).fill(0).map((_, index) => (
        <SkeletonCard key={index}>
          <SkeletonImage />
          <GameInfo>
            <SkeletonShortText />
            <SkeletonText />
            <SkeletonText />
            <SkeletonText style={{ height: '40px' }} />
            <SkeletonShortText />
            <SkeletonShortText />
          </GameInfo>
        </SkeletonCard>
      ))}
    </GameGrid>
  );
};

// 添加延迟函数
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// 添加重试函数
const retry = async (fn: () => Promise<any>, retries = 3, delayMs = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0) throw error;
    await delay(delayMs);
    return retry(fn, retries - 1, delayMs * 2);
  }
};

const GameList: React.FC<{ creatorAddress?: string, isFavorite?: boolean }> = ({ creatorAddress, isFavorite }) => {
  const { t } = useTranslation();
  const [games, setGames] = useState<GameInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [failedImages, setFailedImages] = useState<{ [key: string]: boolean }>(globalFailedImages);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasData, setHasData] = useState(false);
  const publicClient = usePublicClient();
  const navigate = useNavigate();
  const { address, chain } = useAccount();
  const chainId = useChainId();
  const nativeToken = getNativeToken(chainId);
  const wssUrl = getWSSUrl(chainId);
  const [error, setError] = useState<string | null>(null);

  const prevChainId = useRef<number | undefined>(undefined);
  const [nextTimestamp, setNextTimestamp] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [lastPollTimestamp, setLastPollTimestamp] = useState<number>(Math.floor(Date.now() / 1000));

  const getFirstRoundDisplay = (minCap: string, firstRoundInfo: GameInfo['firstRoundInfo'], isRoundActive: boolean) => {
    if (!firstRoundInfo) return 'N/A';
    const minCapValue = parseFloat(minCap);
    const totalDepositValue = parseFloat(ethers.formatEther(firstRoundInfo.totalDeposit));
    const percentage = ((totalDepositValue / minCapValue) * 100).toFixed(2);
    if (isRoundActive) {
      return t('gameList.progress.percent', { percent: percentage });
    } else {
      if (totalDepositValue >= minCapValue) {
        return t('gameList.progress.percent', { percent: '100' });
      } else {
        return `${percentage}% ${t('gameList.progress.failed')}`;
      }
    }
  };

  useEffect(() => {
    // 只在链变化时清空缓存
    if (prevChainId.current !== undefined && prevChainId.current !== chainId) {
      globalGamesCache = {
        creatorGames: {},
        favoriteGames: {},
        allGames: undefined
      };
    }
    prevChainId.current = chainId;

    if (!chain) return;

    // 只在首次加载或链变化时重置状态
    if (isInitialLoad || prevChainId.current !== chainId) {
      setGames([]);
      setHasData(false);
      setNextTimestamp(0);
      setHasMore(true);
    }

    // 拉取数据
    fetchGames();
    
    // eslint-disable-next-line
  }, [chainId, chain, publicClient, creatorAddress, isFavorite, address, navigate]);

  const handleAddressClick = (address: string, type: 'token' | 'creator') => {
    const baseUrl = getBlockExplorerUrl(chainId);
    window.open(`${baseUrl}${address}`, '_blank');
  };

  const handleCardClick = (game: GameInfo) => {
    // navigate(`/game?address=${game.gameAddress}`, { 
    //   state: { tokenAddress: game.tokenAddress }
    // });
    navigate(`/game?address=${game.gameAddress}`, { 
      state: { tokenAddress: game.tokenAddress }
    });
  };

  const handleImageError = (gameAddress: string) => {
    if (!globalFailedImages[gameAddress]) {
      globalFailedImages[gameAddress] = true;
      setFailedImages(prev => ({ ...prev, [gameAddress]: true }));
    }
  };

  const renderImage = useMemo(() => (game: GameInfo) => {
    if (!game?.logoUrl || globalFailedImages[game.gameAddress]) {
      return <img src="/favicon.ico" alt="Default" />;
    }
    return (
      <img 
        src={game.logoUrl} 
        alt={game.tokenName} 
        onError={() => handleImageError(game.gameAddress)}
      />
    );
  }, []);

  const renderVideo = useMemo(() => (game: GameInfo) => {
    if (!game?.logoUrl || !globalFailedImages[game.gameAddress]) return null;
    return (
      <video
        src={game.logoUrl}
        autoPlay
        loop
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
      />
    );
  }, []);

  const fetchGames = async (isLoadMore: boolean = false) => {
    // 如果不是加载更多，先检查缓存
    if (!isLoadMore) {
      let cachedData;
      // 修改读取逻辑
      if (creatorAddress) {
        cachedData = globalGamesCache.creatorGames[creatorAddress];
      } else if (isFavorite === true && address) {  // 明确检查 isFavorite 是否为 true
        cachedData = globalGamesCache.favoriteGames[address];
      } else {
        cachedData = globalGamesCache.allGames;
      }

      // 如果有缓存数据且未过期，直接使用缓存
      if (cachedData && isCacheValid(cachedData.timestamp)) {
        setGames(cachedData.data);
        setNextTimestamp(cachedData.nextTimestamp);
        setHasMore(cachedData.hasMore);
        setHasData(true);
        setLoading(false);
        return;
      }
    }
    
    // 没有缓存或缓存过期，继续获取新数据
    if (!isLoadMore) {
      setError(null);
      setLoading(true);
      setGames([]);
      setHasData(false);
      setNextTimestamp(0);
    } else {
      setIsLoadingMore(true);
    }

    if (!chain) {
      setError( t('gameList.errors.connectWallet'));
      setLoading(false);
      setIsLoadingMore(false);
      return;
    }

    try {
      if (!publicClient?.transport?.url) {
        setLoading(false);
        setIsLoadingMore(false);
        return;
      }
      
      const factoryAddress = getFactoryAddress(chain.id);
      if (!factoryAddress) {
        setError( t('gameList.errors.contractNotDeployed'));
        setLoading(false);
        setIsLoadingMore(false);
        return;
      }

      const provider = new ethers.JsonRpcProvider(publicClient.transport.url);
      const factoryContract = new ethers.Contract(
        factoryAddress,
        [
          'function getGamesWithPagination(uint256 pageSize, uint256 lastTimestamp) external view returns (address[] memory games, uint256 total, uint256 nextTimestamp)',
          'function getGamesByCreatorWithPagination(address creator, uint256 pageSize, uint256 lastTimestamp) external view returns (address[] memory games, uint256 total, uint256 nextTimestamp)',
          'function getFavoriteGamesWithPagination(address user, uint256 pageSize, uint256 lastTimestamp) external view returns (address[] memory games, uint256 total, uint256 nextTimestamp)'
        ],
        provider
      );

      const pageSize = 20;
      let result;
      try {
        if (creatorAddress) {
          result = await factoryContract.getGamesByCreatorWithPagination(creatorAddress, pageSize, nextTimestamp);
        } else if (isFavorite && address) {
          result = await factoryContract.getFavoriteGamesWithPagination(address, pageSize, nextTimestamp);
        } else {
          result = await factoryContract.getGamesWithPagination(pageSize, nextTimestamp);
        }
      } catch (error) {
        console.error('Failed to get game address list:', error);
        setLoading(false);
        setIsLoadingMore(false);
        return;
      }

      const [gameAddresses, totalCount, newNextTimestamp] = result;

      setTotal(totalCount);
      setNextTimestamp(newNextTimestamp);
      const hasMoreData = newNextTimestamp > 0;
      setHasMore(hasMoreData);

      if (!gameAddresses || gameAddresses.length === 0) {
        setLoading(false);
        setIsLoadingMore(false);
        return;
      }

      // 批量处理游戏数据
      const batchPromises = gameAddresses.map(async (gameAddress: string) => {
        try {
          return await retry(async () => {
            const gameContract = new ethers.Contract(
              gameAddress,
              YOLOGame.abi,
              provider
            );
            
            const gameInfo = await gameContract.getGameInfo();
            const tokenContract = new ethers.Contract(
              gameInfo.tokenAddress,
              YOLOToken.abi,
              provider
            );
            
            const tokenInfo = await tokenContract.getTokenInfo();
            const firstRoundInfo = await gameContract.getRoundInfo(1);
            
            const totalRound = Number(gameInfo.totalRounds);
            const fInfoCurrentTime = Number(firstRoundInfo[12]);
            const fInfoMintEndedTime = Number(firstRoundInfo[6]);
            const mintEnded = fInfoCurrentTime > fInfoMintEndedTime;
            let currentRound = Number(gameInfo.currentRoundId);
            let isRoundActive = false;
            if(currentRound > totalRound){
              currentRound = totalRound;
              isRoundActive = false;
            }else{
              isRoundActive = currentRound === 1 
                ? (!mintEnded || 
                   (mintEnded && 
                    parseFloat(ethers.formatEther(firstRoundInfo[1])) >= parseFloat(ethers.formatEther(gameInfo.minCapETH))))
                : (parseFloat(ethers.formatEther(firstRoundInfo[1])) >= parseFloat(ethers.formatEther(gameInfo.minCapETH)));
            }
            // 新增：推断phase
            let phase = undefined;
            if (isRoundActive) {
              const cTime = Number(firstRoundInfo[5]);
              const rDuration = Number(gameInfo.roundDuration);
              const roundIdx = Number(currentRound) - 1;
              if (cTime && rDuration && roundIdx >= 0) {
                const mintStart = cTime + roundIdx * 2 * rDuration;
                const mintEnd = mintStart + rDuration;
                const burnEnd = mintEnd + rDuration;
                if (gameInfo.currentTime >= mintStart && gameInfo.currentTime < mintEnd) {
                  phase = 'minting';
                } else if (gameInfo.currentTime >= mintEnd && gameInfo.currentTime < burnEnd) {
                  phase = 'burning';
                } else {
                  phase = undefined; // 可能是未开始或已结束
                }
              } else {
                phase = 'minting';
              }
            }
            return {
              gameAddress,
              createrAddress: gameInfo.creator,
              tokenAddress: gameInfo.tokenAddress,
              totalRound:totalRound,
              currentRound:currentRound,
              minCap: ethers.formatEther(gameInfo.minCapETH),
              isRoundActive,
              phase, // 新增字段
              tokenName: tokenInfo[0],
              tokenSymbol: tokenInfo[1],
              description: tokenInfo[2],
              logoUrl: tokenInfo[3],
              currentTime: gameInfo.currentTime,
              currentRoundId: gameInfo.currentRoundId,
              firstRoundInfo: {
                totalParticipants: firstRoundInfo[0].toString(),
                totalDeposit: firstRoundInfo[1].toString(),
                totalBurned: firstRoundInfo[2].toString(),
                rewardPool: firstRoundInfo[3].toString(),
                isActive: firstRoundInfo[4],
                startTime:firstRoundInfo[5].toString(),
                mintEndedTime:firstRoundInfo[6].toString(),
                burnEndTime:firstRoundInfo[7].toString(),
                isClaimable:firstRoundInfo[8],
                isRoundEnded:firstRoundInfo[9],
                isSkipped: firstRoundInfo[10],
                minCapEnded: firstRoundInfo[11],
                tokenName: tokenInfo[0],
                tokenSymbol: tokenInfo[1],
                description: tokenInfo[2],
                logoUrl: tokenInfo[3],
                tgLink: tokenInfo[4],
                webLink: tokenInfo[5],
                xLink: tokenInfo[6]
              }
            };
          });
        } catch (error) {
          console.error(`Error fetching info for game ${gameAddress}:`, error);
          return null;
        }
      });

      const batchResults = await Promise.all(batchPromises);
      const validResults = batchResults.filter(result => result !== null);
      
      if (isLoadMore) {
        setGames(prevGames => [...prevGames, ...validResults]);
      } else {
        setGames(validResults);
      }
      
      setHasData(true);
      
      if (isInitialLoad) {  // 移除 !isLoadMore 条件
        const cacheData = { 
          data: isLoadMore ? [...games, ...validResults] : validResults,  // 根据是否加载更多来决定数据
          timestamp: Date.now(),
          nextTimestamp: newNextTimestamp,
          hasMore: hasMoreData
        };
        
        if (creatorAddress) {
          globalGamesCache.creatorGames[creatorAddress] = cacheData;
        } else if (isFavorite && address) {
          globalGamesCache.favoriteGames[address] = cacheData;
        } else {
          if (globalGamesCache.allGames) {
            globalGamesCache.allGames = {
              data: isLoadMore ? [...globalGamesCache.allGames.data, ...validResults] : validResults,
              timestamp: Date.now(),
              nextTimestamp: newNextTimestamp,
              hasMore: hasMoreData
            };
          } else {
            globalGamesCache.allGames = cacheData;
          }
        }
      }
      
    } catch (error) {
      setError(t('gameList.errors.networkError'));
      console.error('Error fetching games:', error);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // 定时刷新活跃游戏数据的 useEffect 保持不变
  useEffect(() => {
    if (!games.length) return;
    const interval = setInterval(async () => {
      // 只刷新 isRoundActive 为 true 的游戏
      const activeGames = games.filter(g => g.isRoundActive);
      if (!activeGames.length) return;
      try {
        const provider = publicClient?.transport?.url ? new ethers.JsonRpcProvider(publicClient.transport.url) : null;
        if (!provider) return;
        const updatedGames = await Promise.all(games.map(async (game) => {
          if (!game.isRoundActive) return game;
          try {
            const gameContract = new ethers.Contract(game.gameAddress, YOLOGame.abi, provider);
            const gameInfo = await gameContract.getGameInfo();
            const firstRoundInfo = await gameContract.getRoundInfo(1);

            // 计算当前状态
            const creatTime = Number(firstRoundInfo[5]);
            const currentTime = Number(gameInfo.currentTime);
            const roundDuration = Number(gameInfo.roundDuration);
            let currentRound = Number(gameInfo.currentRoundId);
            let isRoundActive = true;
            // let cRound = Number(gameInfo.currentRoundId);
            let phase = undefined;
            if(currentRound > gameInfo.totalRounds){
              currentRound = gameInfo.totalRounds;
              isRoundActive = false;
            }else{
              const cTime = Number(creatTime);
              const rDuration = Number(roundDuration);
              const roundIdx = Number(currentRound) - 1;
              if (cTime && rDuration && roundIdx >= 0) {
                const mintStart = cTime + roundIdx * 2 * rDuration;
                const mintEnd = mintStart + rDuration;
                const burnEnd = mintEnd + rDuration;
                if (currentTime >= mintStart && currentTime < mintEnd) {
                  phase = 'minting';
                } else if (currentTime >= mintEnd && currentTime < burnEnd) {
                  phase = 'burning';
                } else {
                  phase = undefined;
                }
              } else {
                phase = 'minting';
              }
              isRoundActive = game.isRoundActive;
            }
            // 只更新部分字段，其他字段保持不变，且所有字段都为string类型
            return {
              ...game,
              currentRound: currentRound,
              isRoundActive: isRoundActive,
              phase, // 新增字段，标记当前状态
              firstRoundInfo: {
                ...game.firstRoundInfo,
                totalParticipants: firstRoundInfo[0]?.toString() || '',
                totalDeposit: firstRoundInfo[1]?.toString() || '',
                totalBurned: firstRoundInfo[2]?.toString() || '',
                rewardPool: firstRoundInfo[3]?.toString() || '',
                isActive: firstRoundInfo[4],
                startTime: firstRoundInfo[5]?.toString() || '',
                mintEndedTime: firstRoundInfo[6]?.toString() || '',
                burnEndTime: firstRoundInfo[7]?.toString() || '',
                isClaimable: firstRoundInfo[8],
                isRoundEnded: firstRoundInfo[9],
                isSkipped: firstRoundInfo[10],
                minCapEnded: firstRoundInfo[11],
                // 保留其他字段
                tokenName: game.firstRoundInfo?.tokenName || '',
                tokenSymbol: game.firstRoundInfo?.tokenSymbol || '',
                description: game.firstRoundInfo?.description || '',
                logoUrl: game.firstRoundInfo?.logoUrl || '',
                tgLink: game.firstRoundInfo?.tgLink || '',
                webLink: game.firstRoundInfo?.webLink || '',
                xLink: game.firstRoundInfo?.xLink || ''
              }
            };
          } catch (e) {
            return game;
          }
        }));
        setGames(updatedGames as GameInfo[]);
      } catch (e) {
        // 忽略错误
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [games, publicClient]);

  // 检查缓存是否过期（比如5分钟）
  const CACHE_EXPIRY = 5 * 60 * 1000; // 5分钟
  const isCacheValid = (timestamp: number) => {
    return Date.now() - timestamp < CACHE_EXPIRY;
  };

  // 添加新的 useEffect 用于定时轮询
  useEffect(() => {
    if (creatorAddress || isFavorite || !chain) return;

    const pollNewGames = async () => {
      try {
        const factoryAddress = getFactoryAddress(chainId);
        if (!factoryAddress) return;

        const provider = new ethers.JsonRpcProvider(publicClient?.transport?.url);
        const factoryContract = new ethers.Contract(
          factoryAddress,
          [
            'function getGamesAfterTimestamp(uint256 timestamp) external view returns (address[] memory)'
          ],
          provider
        );

        // 获取新游戏地址列表
        const newGameAddresses = await factoryContract.getGamesAfterTimestamp(lastPollTimestamp);
        if (newGameAddresses && newGameAddresses.length > 0) {
          // 批量处理新游戏数据
          const batchPromises = newGameAddresses.map(async (gameAddress: string) => {
            try {
              return await retry(async () => {
                const gameContract = new ethers.Contract(gameAddress, YOLOGame.abi, provider);
                const gameInfo = await gameContract.getGameInfo();
                const tokenContract = new ethers.Contract(gameInfo.tokenAddress, YOLOToken.abi, provider);
                const tokenInfo = await tokenContract.getTokenInfo();
                const firstRoundInfo = await gameContract.getRoundInfo(1);

                // 构建游戏数据（使用与 fetchGames 相同的数据结构）
                return {
                  gameAddress,
                  createrAddress: gameInfo.creator,
                  tokenAddress: gameInfo.tokenAddress,
                  totalRound: Number(gameInfo.totalRounds),
                  currentRound: Number(gameInfo.currentRoundId),
                  minCap: ethers.formatEther(gameInfo.minCapETH),
                  isRoundActive: true,
                  phase: 'minting',
                  tokenName: tokenInfo[0],
                  tokenSymbol: tokenInfo[1],
                  description: tokenInfo[2],
                  logoUrl: tokenInfo[3],
                  currentTime: gameInfo.currentTime,
                  currentRoundId: gameInfo.currentRoundId,
                  firstRoundInfo: {
                    totalParticipants: firstRoundInfo[0].toString(),
                    totalDeposit: firstRoundInfo[1].toString(),
                    totalBurned: firstRoundInfo[2].toString(),
                    rewardPool: firstRoundInfo[3].toString(),
                    isActive: firstRoundInfo[4],
                    startTime: firstRoundInfo[5].toString(),
                    mintEndedTime: firstRoundInfo[6].toString(),
                    burnEndTime: firstRoundInfo[7].toString(),
                    isClaimable: firstRoundInfo[8],
                    isRoundEnded: firstRoundInfo[9],
                    isSkipped: firstRoundInfo[10],
                    minCapEnded: firstRoundInfo[11],
                    tokenName: tokenInfo[0],
                    tokenSymbol: tokenInfo[1],
                    description: tokenInfo[2],
                    logoUrl: tokenInfo[3],
                    tgLink: tokenInfo[4],
                    webLink: tokenInfo[5],
                    xLink: tokenInfo[6]
                  }
                };
              });
            } catch (error) {
              console.error(`Error fetching info for new game ${gameAddress}:`, error);
              return null;
            }
          });

          const newGames = (await Promise.all(batchPromises)).filter(game => game !== null);

          if (newGames.length > 0) {
            // 更新状态
            setGames(prevGames => [...newGames, ...prevGames]);
            
            // 更新缓存
            if (globalGamesCache.allGames) {
              globalGamesCache.allGames = {
                data: [...newGames, ...globalGamesCache.allGames.data],
                timestamp: Date.now(),
                nextTimestamp: globalGamesCache.allGames.nextTimestamp,
                hasMore: globalGamesCache.allGames.hasMore
              };
            }
          }
        }
      } catch (error) {
        console.error('Error polling new games:', error);
      }
    };

    // 设置定时器
    const interval = setInterval(() => {
      const currentTimestamp = Math.floor(Date.now() / 1000);
      setLastPollTimestamp(currentTimestamp);
      pollNewGames();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [chainId, chain, publicClient, creatorAddress, isFavorite, lastPollTimestamp]);

  if (!chain) {
    return (
      <GameListContainer>
        <div style={{ color: 'red', textAlign: 'center' }}>Please connect your wallet and select a network</div>
      </GameListContainer>
    );
  }

  if (error) {
    return (
      <GameListContainer>
        <div style={{ color: 'red', textAlign: 'center' }}>{error}</div>
      </GameListContainer>
    );
  }

  if (loading && !hasData) {
    return (
      <GameListContainer>
        <LoadingGameList />
      </GameListContainer>
    );
  }

  return (
    <GameListContainer>
      <GameGrid>
        {games.slice().map((game, index) => {
          const progress = game.firstRoundInfo ? 
            (parseFloat(ethers.formatEther(game.firstRoundInfo.totalDeposit)) / parseFloat(game.minCap)) * 100 : 0;
          
          return (
            <GameCard key={index} onClick={() => handleCardClick(game)}>
              <GameContent>
                <GameImage>
                  {failedImages[game.gameAddress] ? renderVideo(game) : renderImage(game)}
                </GameImage>
                <GameInfo>
                  <TokenHeader>
                    <TokenName>
                    {game?.tokenSymbol && game.tokenSymbol.length > 5
                      ? `${game.tokenSymbol.slice(0, 4)}...` 
                      : game?.tokenSymbol}

                    </TokenName>
                    <RoundInfo isActive={game.isRoundActive} phase={game.phase}>
                    {game.isRoundActive
                      ? `${game.currentRound} / ${game.totalRound}`
                      : (parseFloat(ethers.formatEther(game.firstRoundInfo?.totalDeposit || '0')) >= parseFloat(game.minCap)
                          ? `${game.currentRound} / ${game.totalRound}`
                          : `1 / ${game.totalRound}`)}
                    </RoundInfo>
                  </TokenHeader>
                  <Description>
                    {game?.description && game.description.length > 100 
                      ? `${game.description.slice(0, 100)}...` 
                      : game?.description}
                  </Description>
                  <AddressWrapper>
                  {t('gameList.address')}
                    <AddressInfo 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddressClick(game.tokenAddress, 'token');
                      }}
                    >
                      {formatAddress(game.tokenAddress)}
                    </AddressInfo>
                  </AddressWrapper>
                  <AddressWrapper>
                  {t('gameList.creator')}
                    <AddressInfo 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddressClick(game.createrAddress, 'creator');
                      }}
                    >
                      {formatAddress(game.createrAddress)}
                    </AddressInfo>
                  </AddressWrapper>
                  <MinCap>{t('gameList.minCap')} {game.minCap} {nativeToken}</MinCap>
                  
                </GameInfo>
              </GameContent>
              <ProgressWrapper>
                <ProgressBarWrapper>
                  <ProgressBar progress={progress} />
                </ProgressBarWrapper>
                <FristRound>{getFirstRoundDisplay(game.minCap, game.firstRoundInfo, game.isRoundActive)}</FristRound>
              </ProgressWrapper>
            </GameCard>
          );
        })}
      </GameGrid>
      {hasMore && (
        <LoadMoreButton 
          onClick={() => fetchGames(true)}
          disabled={isLoadingMore}
        >
          {isLoadingMore ? t('gameList.loading') : t('gameList.loadMore')}
        </LoadMoreButton>
      )}
    </GameListContainer>
  );
};

const LoadMoreButton = styled.button`
  display: block;
  margin: 20px auto;
  padding: 8px 16px;
  background: #4ad0ac;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  
  &:hover {
    background: #3db89a;
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

export default GameList; 